<template>
  <main class="main page-inside products-page">
    <div class="main-wrapper">
      <div class="section shop-section">
        <div class="wrapper">
          <div class="title big">{{ $t("SHOP") }}</div>

          <!-- Categories -->
          <div class="categories-section">
            <div class="category-buttons">
              <button
                v-for="category in displayCategories"
                :key="category.id"
                :class="[
                  'category-btn',
                  {
                    active: activeCategory && activeCategory.id === category.id,
                  },
                ]"
                @click="selectCategory(category)"
              >
                {{ $t(category.text) }}
              </button>
              <button
                class="filter-btn"
                :class="{ active: showSales }"
                @click="toggleSales"
              >
                {{ $t("SALES") }}
              </button>
              <button
                class="filter-btn"
                :class="{ active: showBestsellers }"
                @click="toggleBestsellers"
              >
                {{ $t("OUR BESTSELLERS") }}
              </button>
            </div>
          </div>

          <!-- Sorting and Search -->
          <div class="filters">
            <div class="sort-section">
              <span class="sort-label">{{ $t("Sort by Price:") }}</span>
              <div class="sort-dropdown" ref="sortDropdown">
                <button class="sort-button" @click.stop="toggleSortDropdown">
                  {{ $t(currentSortText) }}
                  <span class="sort-arrow">▼</span>
                </button>
                <div class="sort-options" v-if="showSortDropdown">
                  <button
                    v-for="option in sortOptions"
                    :key="option.id"
                    :class="['sort-option', { active: sort === option.id }]"
                    @click="selectSort(option)"
                  >
                    {{ $t(option.text) }}
                  </button>
                </div>
              </div>
            </div>
            <div class="search">
              <div class="search-input-container">
                <img src="../images/glass.svg" class="search-icon" />
                <input
                  type="text"
                  placeholder=""
                  v-model="searchedProductText"
                  @keyup.enter="searchSubmit"
                />
              </div>
              <button class="search-button" @click="searchSubmit">
                {{ $t("SEARCH") }}
              </button>
            </div>
          </div>
          <transition name="fade">
            <div class="product-list" v-if="productList">
              <div
                class="product-card"
                v-for="item in productList.data"
                :key="item.id"
                @click="openProductModal(item)"
              >
                <div class="product-image">
                  <img :src="imgDomain + item.img + '.png'" :alt="item.name" />
                </div>
                <div class="product-bottom">
                  <div class="product-name">{{ item.name }}</div>
                  <div class="product-prices">
                    <div
                      class="product-original-price"
                      v-if="item.old_price && item.old_price > item.price"
                    >
                      {{ currency }} {{ item.old_price.toFixed(2) }}
                    </div>
                    <div class="product-price">
                      {{ currency }} {{ item.price.toFixed(2) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
          <transition name="fade">
            <div
              class="title-tiny no-products"
              v-if="productList.data && !productList.data.length"
            >
              {{ $t("No products") }}
            </div>
          </transition>
          <div class="pagination-container" v-if="totalProducts">
            <button
              class="pagination-nav pagination-prev"
              @click="prevPage"
              :disabled="activePage <= 1"
            >
              <img src="../assets/levo.svg" alt="Previous" />
            </button>
            <div class="pagination-numbers">
              <button
                v-for="(page, index) in visiblePages"
                :key="typeof page === 'number' ? page : 'ellipsis-' + index"
                :class="['pagination-number', { active: page === activePage }]"
                @click="goToPage(page)"
                :disabled="page === '...'"
              >
                {{ page }}
              </button>
            </div>
            <button
              class="pagination-nav pagination-next"
              @click="nextPage"
              :disabled="activePage >= totalPages"
            >
              <img src="../assets/levo.svg" alt="Next" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: "Products",
  props: ["currency", "currencyCode"],
  components: {},
  data: function () {
    return {
      imgDomain: "",
      productList: [],
      activePage: 1,
      totalProducts: null,
      searchedProductText: "",
      activeCategory: { id: 0, text: "ALL" },
      sort: "price",
      categories: [],
      showSales: false,
      showBestsellers: false,
      showSortDropdown: false,
      sortOptions: [
        { id: "price", text: "LOW TO HIGH" },
        { id: "-price", text: "HIGH TO LOW" },
      ],
    };
  },
  computed: {
    displayCategories() {
      return this.categories.length > 1
        ? this.categories
        : [
            { id: 0, text: "ALL" },
            { id: 1, text: "AMMO" },
            { id: 2, text: "RESOURCES" },
            { id: 3, text: "TOOLS" },
            { id: 4, text: "MEDICINE" },
            { id: 5, text: "APPAREL" },
            { id: 6, text: "TURELS" },
            { id: 7, text: "FESTIVE" },
            { id: 8, text: "WEAPON" },
            { id: 9, text: "OTHER" },
          ];
    },
    totalPages() {
      return Math.ceil(this.totalProducts / 24);
    },
    currentSortText() {
      const option = this.sortOptions.find((o) => o.id === this.sort);
      return option ? option.text : "LOW TO HIGH";
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.activePage;

      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const pages = [];
      const rangeSize = 2; // страниц по бокам от текущей

      pages.push(1);

      const rangeStart = Math.max(2, current - rangeSize);
      const rangeEnd = Math.min(total - 1, current + rangeSize);

      if (rangeStart > 2) {
        pages.push("...");
      }

      for (let i = rangeStart; i <= rangeEnd; i++) {
        pages.push(i);
      }

      if (rangeEnd < total - 1) {
        pages.push("...");
      }

      if (total > 1) {
        pages.push(total);
      }

      return pages;
    },
  },
  watch: {
    currencyCode: function () {
      this.filterProducts();
    },
  },
  mounted() {
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
    this.getCategories();
    this.filterProducts();
    document.addEventListener("click", this.handleClickOutsideSort);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutsideSort);
  },
  methods: {
    getCategories() {
      this.$http
        .get(process.env.VUE_APP_API + "categories")
        .then((res) => {
          const apiCategories = (res.data.payload || []).map(
            ({ id, name }) => ({
              id,
              text: name,
            }),
          );
          this.categories = [{ id: 0, text: "ALL" }, ...apiCategories];
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.$parent.openSignInModal();
          }
        });
    },
    selectSort(option) {
      this.sort = option.id;
      this.showSortDropdown = false;
      this.filterProducts();
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    handleClickOutsideSort(event) {
      if (
        this.$refs.sortDropdown &&
        !this.$refs.sortDropdown.contains(event.target)
      ) {
        this.showSortDropdown = false;
      }
    },
    selectCategory(category) {
      this.showSales = false;
      this.showBestsellers = false;
      this.activeCategory = category;
      this.activePage = 1;
      this.filterProducts();
    },
    goToProduct(item) {
      this.$parent.goToProduct(item);
    },
    changePage(page) {
      this.activePage = page;
      this.filterProducts();
    },
    searchSubmit() {
      this.filterProducts();
    },
    filterProducts() {
      let addSort = "sort=" + this.sort;
      let addPage = "&page=" + this.activePage;
      let addSearch = "&filter[name]=" + this.searchedProductText;
      let addSales = this.showSales ? "&sales=1" : "";
      let addBestsellers = this.showBestsellers ? "&bestsellers=1" : "";

      let addcCategory;

      if (!this.activeCategory || this.activeCategory.id == 0) {
        addcCategory = "";
      } else {
        addcCategory = "&filter[category]=" + this.activeCategory.id;
      }

      this.$http
        .get(
          process.env.VUE_APP_API +
            "items?" +
            addSort +
            addPage +
            addSearch +
            addcCategory +
            addSales +
            addBestsellers +
            "&per_page=24",
        )
        .then((res) => {
          this.productList = res.data;
          this.totalProducts = res.data.meta.total;
        })
        .catch(() => {});
    },
    openProductModal(product) {
      this.$emit("openProductModal", product);
    },
    prevPage() {
      if (this.activePage > 1) {
        this.activePage--;
        this.filterProducts();
      }
    },
    nextPage() {
      if (this.activePage < this.totalPages) {
        this.activePage++;
        this.filterProducts();
      }
    },
    goToPage(page) {
      if (page !== "..." && page !== this.activePage) {
        this.activePage = page;
        this.filterProducts();
      }
    },
    toggleSales() {
      this.activeCategory = "";
      this.activePage = 1;
      this.showSales = true;
      this.showBestsellers = false;
      this.filterProducts();
    },
    toggleBestsellers() {
      this.activeCategory = "";
      this.activePage = 1;
      this.showBestsellers = true;
      this.showSales = false;
      this.filterProducts();
    },
  },
};
</script>
